import {writable} from 'svelte/store';
import axios from "axios";
import {router} from "@inertiajs/svelte";

// Danbooru-style tag names → search-friendly keywords: drop the
// "(qualifier)" disambiguation suffix, underscores → spaces.
// Mirror of MetaTagsHelper.clean_tag_names — keep the two in sync.
export const cleanTagNames = (names) =>
    [...new Set(names.map(n => n.replace(/_\(.+\)$/, '').replaceAll('_', ' ').trim()))];

// Alt text for a media asset image. Caption first: it is the only source that
// describes a textless image, and it is the one Google Images can read. OCR
// alone left every no-text meme with an empty alt.
export const imageAlt = (ma) => {
    if (!ma) return '';
    const cap = ma.caption || {};
    return [
        cap.caption,
        cap.caption_en,
        ma.ocr_text,
        cleanTagNames((ma.tags || []).map(t => t.name)).join(', '),
    ].filter(Boolean).join(' · ');
};

// searchQueryId: the id page 1 was recorded under. Sending it back lets the
// server append this page's results to that search's impression list, so a
// click further down is still attributable to the ranking that produced it.
export const fetchMediaAssets = async (filters, page, searchQueryId = null) => {
    const resp = await axios.get('/m', {
        headers: {
            "X-Inertia": true
        },
        params: {
            ...filters,
            page: page,
            search_query_id: searchQueryId
        }
    });
    return resp.data.props;
}

export const fetchRelatedPosts = async (id, filters, page) => {
    const resp = await axios.get(`/p/${id}/related`, {
        params: {
            ...filters,
            page: page
        }
    });
    return resp.data;
}

export const fetchRelatedMediaAssets = async (id, filters, page) => {
    const resp = await axios.get(`/m/${id}/related`, {
        params: {
            ...filters,
            page: page
        }
    });
    return resp.data;
}

// searchQueryId: see fetchMediaAssets.
export const fetchPosts = async (filters, page, searchQueryId = null) => {
    const resp = await axios.get('/p', {
        headers: {
            "X-Inertia": true
        },
        params: {
            ...filters,
            page: page,
            search_query_id: searchQueryId
        }
    });
    return resp.data.props;
}



export const fetchTagPosts = async (selected, page, q) => {
    const params = { selected, page };
    if (q) params.q = q;
    const resp = await axios.get('/tags/posts', { params });
    return resp.data;
}

export const fetchTagMediaAssets = async (selected, page, q) => {
    const params = { selected, page };
    if (q) params.q = q;
    const resp = await axios.get('/tags/media_assets', { params });
    return resp.data;
}

export const fetchTagSimilarMediaAssets = async (selected, page, q) => {
    const params = { selected, page };
    if (q) params.q = q;
    const resp = await axios.get('/tags/similar_media_assets', { params });
    return resp.data;
}

export const fetchTagSimilarPosts = async (selected, page, q) => {
    const params = { selected, page };
    if (q) params.q = q;
    const resp = await axios.get('/tags/similar_posts', { params });
    return resp.data;
}

export const fetchUserPosts = async (userId, page) => {
    const resp = await axios.get(`/users/${userId}/posts`, { params: { page } });
    return resp.data;
}

export const fetchUserMediaAssets = async (userId, page) => {
    const resp = await axios.get(`/users/${userId}/media_assets`, { params: { page } });
    return resp.data;
}

export const fetchTags = async (tag_q, page) => {
    const resp = await axios.get('/tags', {
        headers: { "X-Inertia": true },
        params: { tag_q, page }
    });
    return resp.data.props;
}

// Fire-and-forget search-result click beacon (first-party history, roadmap
// P0-B). keepalive lets the request survive the page navigation; every
// failure path is swallowed — recording must never affect the click.
// kind: 'click' (looked promising) or 'save' (kept it) — a save is the much
// stronger relevance signal, so the two are recorded separately.
//
// No position argument on purpose: the server derives the rank from the
// search's recorded impressions. The grid's own index was computed over the
// infinite-scroll-accumulated array and was wrong for everything past page 1.
export function reportSearchClick(searchQueryId, targetType, targetId, kind = 'click') {
    if (!searchQueryId) return;
    try {
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        fetch('/search_clicks', {
            method: 'POST',
            keepalive: true,
            headers: {'Content-Type': 'application/json', 'X-CSRF-Token': token || ''},
            body: JSON.stringify({
                search_query_id: searchQueryId,
                target_type: targetType,
                target_id: targetId,
                kind,
            }),
        }).catch(() => {});
    } catch (_) { /* never block navigation */ }
}

export const imageCopied = writable(false);

// Copies a single URL to the clipboard and flashes the imageCopied toast.
// Used for the "Copy link" overlay button — it copies the page URL, which
// unfurls to a card on chat apps and carries the backlink here.
export async function copyImageURL(path, contentId = path.split('/').filter(Boolean).at(-1)) {
    const url = new URL(path, window.location.origin).href;
    await navigator.clipboard.writeText(url);
    imageCopied.set(true);
    setTimeout(() => {
        imageCopied.set(false);
    }, 500);
}

export const copyImage = async (imageUrl, contentId) => {
    // References:
    // Copy image to clipboard: https://stackoverflow.com/a/75266797
    // ClipboardItem: https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem/ClipboardItem
    // Dict key from variable: https://stackoverflow.com/questions/10640159/key-for-javascript-dictionary-is-not-stored-as-value-but-as-variable-name
    const data = await fetch(imageUrl);
    const blob = await data.blob();
    try {
        await navigator.clipboard.write([
            new ClipboardItem({[blob.type]: blob})
        ]);
    } catch (error) {
        // [html - How to render a blob on a canvas element? - Stack Overflow](https://stackoverflow.com/questions/38004917/how-to-render-a-blob-on-a-canvas-element)
        console.log('Copy image with canvas');
        const canvas = document.createElement("canvas");
        let img = new Image();
        img.src = URL.createObjectURL(blob);
        img.onload = function () {
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0, img.width, img.height);
            canvas.toBlob((blob) => {
                navigator.clipboard.write([
                    new ClipboardItem({"image/png": blob})
                ]);
            }, "image/png");
            console.log('copied!');
        }
    }
    imageCopied.set(true);
    setTimeout(() => {
        imageCopied.set(false);
    }, 500);
}

export async function getImageDimensions(file) {
    try {
        let img = new Image();
        img.src = URL.createObjectURL(file);
        await img.decode();
        let width = img.width;
        let height = img.height;
        return {
            width,
            height,
        };
    } catch (e) {
        console.log(e);
        return {width: 200, height: 100};
    }
}

/** Get the profile URL for a user object. Prefers /@username, falls back to /users/:id */
export function userUrl(user) {
    if (user.username) return `/@${user.username}`;
    return `/users/${user.id}`;
}

export async function getImagesDimensions(files) {
    if (!files) {
        return [];
    }
    // Get dimensions with getImageDimensions
    let imgDimensions = [];
    for (let file of files) {
        imgDimensions.push(await getImageDimensions(file));
    }
    return imgDimensions;
}
